import { useState } from "react";
import { Position } from "@/services/positionService";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { EventPositionDTO } from "@/pages/admin/events/types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loading";
import { Card } from "@/components/ui/card";
import { Plus, Minus, Trash2, Edit2 } from "lucide-react";
import { AxiosError } from "axios";

interface ErrorResponse {
  message: string;
  error?: string;
}

interface ManagePositionsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  currentPositions: EventPositionDTO[];
  availablePositions: Position[];
  onPositionsUpdated: () => void;
}

export function ManagePositionsDialog({
  isOpen,
  onOpenChange,
  eventId,
  currentPositions,
  availablePositions,
  onPositionsUpdated,
}: ManagePositionsDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [editingQuantities, setEditingQuantities] = useState<
    Record<string, number>
  >({});

  const handleAddPosition = async () => {
    if (!selectedPosition || quantity < 1) {
      toast({
        title: "Error",
        description: "Please select a position and enter a valid quantity",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_API_URL}event/${eventId}/position/add`,
        {
          positionId: selectedPosition,
          quantity: quantity,
        },
      );

      toast({
        title: "Success",
        description: "Position added successfully",
        variant: "success",
      });
      onPositionsUpdated();
      setSelectedPosition("");
      setQuantity(1);
    } catch (error) {
      const errorMessage: string = error.response.data.error;
      toast({
        title: "Error",
        description: "Failed to add position. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (
    positionId: string,
    newQuantity: number,
  ) => {
    if (newQuantity < 0) return;

    setLoading(true);
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_BASE_API_URL}event/${eventId}/${positionId}/edit`,
        {
          eventPositionId: positionId,
          quantity: newQuantity,
        },
      );

      toast({
        title: "Success",
        description:
          "Position quantity updated successfully. Changes are automatically reflected.",
        variant: "success",
      });
      onPositionsUpdated();
      setEditingQuantities({});
      onOpenChange(false);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      const errorMessage =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        "Failed to update position quantity";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePosition = async (positionId: string) => {
    setLoading(true);
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_BASE_API_URL}event/${eventId}/position/${positionId}`,
      );

      toast({
        title: "Success",
        description: "Position removed successfully",
        variant: "success",
      });
      onPositionsUpdated();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove position",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (positionId: string, currentQuantity: number) => {
    setEditingQuantities((prev) => ({
      ...prev,
      [positionId]: currentQuantity,
    }));
  };

  const updateEditingQuantity = (positionId: string, value: number) => {
    setEditingQuantities((prev) => ({
      ...prev,
      [positionId]: value,
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Event Positions</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="space-y-4 pb-4">
            <h4 className="font-medium text-sm text-muted-foreground">
              Add New Position
            </h4>
            <div className="grid gap-4">
              <Select
                value={selectedPosition}
                onValueChange={setSelectedPosition}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a position" />
                </SelectTrigger>
                <SelectContent>
                  {availablePositions
                    .sort((a, b) => a.tableName.localeCompare(b.tableName))
                    .map((position) => (
                      <SelectItem key={position.id} value={position.id}>
                        {position.tableName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <div className="flex items-center border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(Math.max(1, Number(e.target.value)))
                    }
                    className="w-20 border-0 text-center"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setQuantity((prev) => prev + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  onClick={handleAddPosition}
                  disabled={loading || !selectedPosition}
                  className="flex-1"
                >
                  {loading ? <Loading size="sm" /> : "Add Position"}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <h4 className="font-medium text-sm text-muted-foreground">
              Current Positions
            </h4>
            <div className="space-y-2 overflow-y-auto flex-1 pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-secondary/20 [&::-webkit-scrollbar-thumb]:bg-secondary/40 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-secondary/60">
              {currentPositions
                .sort((a, b) => a.tableName.localeCompare(b.tableName))
                .map((position) => (
                  <Card
                    key={position.eventPositionId}
                    className="p-3 shadow-none mb-0"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {position.tableName}
                        </p>
                      </div>

                      {editingQuantities[position.eventPositionId] !==
                      undefined ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center border rounded-md">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                updateEditingQuantity(
                                  position.eventPositionId,
                                  Math.max(
                                    0,
                                    editingQuantities[
                                      position.eventPositionId
                                    ] - 1,
                                  ),
                                )
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              value={
                                editingQuantities[position.eventPositionId]
                              }
                              onChange={(e) =>
                                updateEditingQuantity(
                                  position.eventPositionId,
                                  Math.max(0, Number(e.target.value)),
                                )
                              }
                              className="w-16 border-0 text-center"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                updateEditingQuantity(
                                  position.eventPositionId,
                                  editingQuantities[position.eventPositionId] +
                                    1,
                                )
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleUpdateQuantity(
                                position.eventPositionId,
                                editingQuantities[position.eventPositionId],
                              )
                            }
                            disabled={loading}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setEditingQuantities((prev) => {
                                const newState = { ...prev };
                                delete newState[position.eventPositionId];
                                return newState;
                              })
                            }
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-muted-foreground whitespace-nowrap">
                            Qty: {position.quantity}
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() =>
                              startEditing(
                                position.eventPositionId,
                                position.quantity,
                              )
                            }
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() =>
                              handleRemovePosition(position.eventPositionId)
                            }
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
